import { create } from "zustand";
import type { MenuItem } from "@/types";

// 메뉴 캐시 — 새로고침하면 사용자조회·메뉴조회 두 번의 통신이 끝나야 메뉴가 떠서,
// 그 사이 왼쪽 메뉴를 눌러도 아무 일이 없었다 (2026-10-08 증상).
// 지난번에 받은 메뉴를 저장해 두고 화면이 붙자마자 그려 준다. 통신이 끝나면 덮어쓴다.
//
// ⚠ 초기 상태에 캐시를 바로 넣으면 안 된다 — 서버는 빈 메뉴로, 브라우저는 캐시로 그려
//   하이드레이션이 깨지고(React #418) 화면 전체가 안 눌린다. 실제로 그렇게 터뜨렸다.
//   반드시 loadCache() 를 effect 에서 호출한다.
//
// ⚠ 캐시는 **그 사람 것**이다 (2026-10-14).
//   한 PC 에서 계정을 바꿔 로그인하면 앞사람 메뉴가 그대로 떴다. 캐시에 userId 를 함께
//   적어 두고, 지금 로그인한 사람 것이 아니면 쓰지 않고 지운다.
const CACHE_KEY = "fito_menu_cache_v2";
const OLD_CACHE_KEYS = ["fito_menu_cache_v1"];   // 주인 표시가 없던 옛 캐시 — 보이면 지운다

type MenuCache = {
  userId: string;
  topMenus: MenuState["topMenus"];
  sideMenus: MenuItem[];
  activeTopMenu: string;
};

function readCache(): MenuCache | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as MenuCache) : null;
  } catch {
    return null; // 사생활 보호 모드 등 저장소를 못 쓰는 경우
  }
}

/** 저장해 둔 메뉴를 모두 지운다 — 로그인·로그아웃 때 부른다 */
export function clearMenuCache() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CACHE_KEY);
    for (const k of OLD_CACHE_KEYS) window.localStorage.removeItem(k);
  } catch {
    // 저장소를 못 쓰면 캐시도 없다
  }
}

function writeCache(patch: Partial<MenuCache>) {
  if (typeof window === "undefined") return;
  const owner = cacheOwner;
  if (!owner) return;   // 누구 것인지 모르면 적지 않는다
  try {
    const prev = readCache();
    const base: MenuCache =
      prev && prev.userId === owner ? prev : { userId: owner, topMenus: [], sideMenus: [], activeTopMenu: "" };
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ ...base, ...patch, userId: owner }));
  } catch {
    // 저장 실패는 무시 — 화면 동작에는 지장이 없다
  }
}

// 지금 화면을 보고 있는 사람 — loadCache(userId) 가 정한다
let cacheOwner = "";

interface MenuState {
  topMenus: { OBJID: string; MENU_NAME_KOR: string }[];
  sideMenus: MenuItem[];
  activeTopMenu: string;
  activeSubMenu: string;
  isCollapsed: boolean;
  setTopMenus: (menus: { OBJID: string; MENU_NAME_KOR: string }[]) => void;
  setSideMenus: (menus: MenuItem[]) => void;
  setActiveTopMenu: (id: string) => void;
  setActiveSubMenu: (id: string) => void;
  toggleCollapsed: () => void;
  /**
   * 저장해 둔 메뉴를 불러온다 — 반드시 화면이 붙은 뒤에, **로그인한 사람을 알고 나서** 부른다.
   * 그 사람 것이 아니면 쓰지 않고 지운다 (계정을 바꿔 로그인했을 때 앞사람 메뉴 방지).
   */
  loadCache: (userId: string) => void;
  fetchTopMenus: () => Promise<void>;
  fetchSideMenus: (menuObjId: string) => Promise<void>;
}

export const useMenuStore = create<MenuState>((set) => ({
  topMenus: [],
  sideMenus: [],
  activeTopMenu: "",
  activeSubMenu: "",
  isCollapsed: false,

  setTopMenus: (topMenus) => set({ topMenus }),
  setSideMenus: (sideMenus) => set({ sideMenus }),
  setActiveTopMenu: (activeTopMenu) => set({ activeTopMenu }),
  setActiveSubMenu: (activeSubMenu) => set({ activeSubMenu }),
  toggleCollapsed: () => set((s) => ({ isCollapsed: !s.isCollapsed })),

  loadCache: (userId: string) => {
    const id = String(userId || "");
    if (!id) return;
    cacheOwner = id;
    const c = readCache();
    if (!c || c.userId !== id) {
      // 앞사람(또는 주인 모를 옛 버전) 캐시 — 쓰지 않고 지운다. 메뉴는 서버 응답으로만 채운다.
      clearMenuCache();
      return;
    }
    // 이미 서버에서 받아온 메뉴가 있으면 캐시로 덮지 않는다
    set((s) => (s.sideMenus.length > 0
      ? s
      : { topMenus: c.topMenus ?? [], sideMenus: c.sideMenus ?? [], activeTopMenu: c.activeTopMenu ?? "" }));
  },

  fetchTopMenus: async () => {
    const res = await fetch("/api/menu/top");
    if (res.ok) {
      const data = await res.json();
      set({ topMenus: data.menus || [] });
      writeCache({ topMenus: data.menus || [] });
    }
  },

  fetchSideMenus: async (menuObjId: string) => {
    const res = await fetch("/api/menu", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ MENUOBJID: menuObjId }),
    });
    if (res.status === 401) {
      // 세션이 끝났다. 가만히 있으면 메뉴를 눌러도 아무 일이 없어 고장처럼 보인다.
      if (typeof window !== "undefined") window.location.href = "/login";
      return;
    }
    if (res.ok) {
      const data = await res.json();
      const items = (data.RESULT || []) as MenuItem[];

      // 트리 구조 빌드 (menu.jsp add_menu 로직 대응).
      // 2026-10-02: 최상위 아래 3단까지 세운다. 종전에는 1단/2단 둘로만 나눠 담아
      // 3단 메뉴가 통째로 사라졌다.
      const byParent = new Map<string, MenuItem[]>();
      items.forEach((i) => {
        if (i.level === "1") return; // 1단(대메뉴)은 아래에서 따로 모은다
        const list = byParent.get(i.parentObjId) || [];
        list.push(i);
        byParent.set(i.parentObjId, list);
      });

      const bySeq = (a: MenuItem, b: MenuItem) =>
        (parseInt(a.menuOrder || "0", 10) || 0) - (parseInt(b.menuOrder || "0", 10) || 0);

      // 아래에서 위로 세운다 — 들어갈 데가 없는 빈 폴더(URL 도 하위도 없음)는 버린다
      const build = (parentObjId: string): MenuItem[] => {
        const kids = [...(byParent.get(parentObjId) || [])].sort(bySeq);
        // 동일 메뉴명 중복 제거 (SEQ 순서 유지, 첫 번째만 유지)
        const seen = new Set<string>();
        const out: MenuItem[] = [];
        for (const k of kids) {
          if (seen.has(k.menuNameKor)) continue;
          seen.add(k.menuNameKor);
          const children = build(k.objid);
          if (!k.menuUrl && children.length === 0) continue;
          out.push({ ...k, children });
        }
        return out;
      };

      const tree = items
        .filter((i) => i.level === "1")
        .sort(bySeq)
        .map((p) => ({ ...p, children: build(p.objid) }))
        .filter((p) => p.menuUrl || p.children.length > 0);

      // 볼 메뉴가 한 줄도 없으면 지금 메뉴를 **지우지 않는다**. 지워 버리면 왼쪽이
      // 텅 비어 그 뒤로 누를 것이 없어진다 — 사용자에게는 "메뉴 클릭 안 됨"이다.
      // (빈 최상위는 /api/menu/top 에서 이미 걸러 내지만, 권한이 바뀌는 순간 등을 위한 이중 안전장치)
      if (tree.length === 0) return;
      set({ sideMenus: tree, activeTopMenu: menuObjId });
      writeCache({ sideMenus: tree, activeTopMenu: menuObjId });
    }
  },
}));
